import { useTranslations } from 'next-intl';
import React from 'react';

import ContentTitle from '@/components/ContentTitle';
import MovieDescription from '@/components/MovieDescription';

import { HrefOrHrefWithParams } from 'next-intl/dist/types/navigation/shared/utils';

import { CachedContentData } from '@/hooks/useCachedContentData';

import ImageWithTransition from '@cineamo/ui/src/image/ImageWithTransition';

import { Link } from '@/i18n/routing';

import AllShowingDaysSlider from './AllShowingDaysSlider';

type ProgramListItemLayoutProps = {
    href?: HrefOrHrefWithParams<'/program/[contentId]'>;
    content: CachedContentData;
    totalItems: number;
    selectedDate?: string | 'all';
};

export function ProgramDefaultListItemLayout(props: ProgramListItemLayoutProps): React.JSX.Element {
    const { href, content, totalItems, selectedDate } = props;
    const t = useTranslations();

    return (
        <div
            className="flex flex-row space-x-24 py-[24px]"
            role="group"
            aria-label={content.title}>
            <Link
                className="cursor-pointer w-auto h-full min-w-[120px] aspect-[2/3]"
                href={href ?? ''}
                /*
                 * Prefetch the full page data beyond the loading.js boundaries by setting the prefetch prop to true
                 * (See: https://nextjs.org/docs/app/building-your-application/routing/linking-and-navigating#2-prefetching)
                 */
                aria-label={content.title}
                prefetch={false}>
                <ImageWithTransition
                    className="rounded-4"
                    width={120}
                    height={180}
                    src={content.posterImage ?? ''}
                    alt={content.title ?? ''}
                />
            </Link>
            <div className="w-[480px] shrink-0 flex flex-col space-y-16">
                <ContentTitle
                    href={href}
                    content={content}
                    showMovieDuration
                    showGenres
                />
                <Link
                    href={href ?? ''}
                    className="flex-auto max-w-[600px] text-[rgba(var(--on-background-color-rgb),0.8)]">
                    <MovieDescription
                        content={content}
                        maxLinesDesktop={3}
                    />
                </Link>
                <div className="text-body-bold-12 italic text-[rgba(var(--on-background-color-rgb),0.8)]">
                    {t('specials.n-showings', { count: totalItems })}
                </div>
            </div>
            <AllShowingDaysSlider
                content={content}
                selectedDate={selectedDate}
            />
        </div>
    );
}
